import "../../../styles/homePage/dashboard.scss";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TableHeaderCards from "../../../components/patientsList/genericTable/TableHeaderCards";
import { svgs } from "../../../common/assats";
import GraphCard from "../../../components/graphCards/graphCards";
import Chart from "react-apexcharts";
import TableHeader from "../../../components/patientsList/genericTable/TableHeader";
import { chartConfiguration } from "./Dashboard-charts-configuration";
import {
  exportDashboardData,
  getDashboardData,
} from "../../../common/api/apiCalls";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../common/contexts/AlertContext";
import RangeDatePicker from "../../../components/patientsList/genericTable/DatePicker";
import { DateRange } from "react-day-picker";
import TableFilters from "../../../components/patientsList/genericTable/TableFilters";
import Loader from "../../../components/Loader/Loader";
import { getAllSites } from "../../../common/api/siteService";
import { GenericRecord } from "../../../common/types/filterObjType";
import { DeviceErrorMessages } from "../../../common/types/deviceErrorMap";
import { useUser } from "../../../common/contexts/UserContext";
import { roles } from "../../../common/types/roles";

const Dashboard = () => {
  const [filters, setFilters] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<DateRange | undefined>(
    undefined
  );
  const { t } = useTranslation();
  const alert = useAlert();
  const getText = (text: string) => t(`dashboard.${text}`);
  const { user } = useUser();
  const [cards, setCards] = useState([
    {
      id: "hcp",
      headline: getText("cards.hcp"),
      icon: svgs.cards.PatientsVerified,
      value: 0,
      action: () => console.log("HCP"),
    },
    {
      id: "sites",
      headline: getText("cards.centers"),
      icon: svgs.cards.Centers,
      value: 0,
      action: () => console.log("centers"),
    },
    {
      id: "patients",
      headline: getText("cards.patients"),
      icon: svgs.cards.UserAddIcon,
      value: 0,
      action: () => console.log("patients"),
    },
    {
      id: "averageNewPatientsPerWeek",
      headline: getText("cards.averageNewPatientsPerWeek"),
      icon: svgs.cards.Graph,
      value: 0,
      action: () => console.log("average"),
    },
    {
      id: "devices",
      headline: getText("cards.devices"), // Add Sites Card
      icon: svgs.cards.DevicesIcon,
      value: 0, // Placeholder value
      action: () => console.log("sites"),
    },
    {
      id: "errors",
      headline: getText("cards.errors"),
      icon: svgs.cards.ErrorIcon,
      value: 0,
      action: () => console.log("errors"),
    },
  ]);

  const [activeVsNonActive, setActiveVsNonActive] = useState<number[]>([]);
  const [deviceStatus, setDeviceStatus] = useState<number[]>([]);

  const {
    data: filtersData,
    error: siteDataError,
    isLoading: isCenterDataLoading,
  } = useQuery({
    queryKey: ["centerData"],
    queryFn: getAllSites,
  });
  function getFilters(data: { id: string; SiteName: string }[]) {
    if (!data || data.length === 0 || !Array.isArray(data)) {
      return {};
    }
    let filterObj: GenericRecord<string> = {
      center: data.map((val) => {
        return { name: val.SiteName, value: val.SiteName };
      }),
    };
    return filterObj;
  }

  const series2 = [320, 400, 10, 300, 1000];

  const {
    data: dashboardsData,
    error: dashboardsError,
    isLoading: dashboardsIsLoading,
  } = useQuery({
    queryKey: ["dashboards", dateFilter, filters],
    queryFn: () => getDashboardData(dateFilter, filters),
  });
  const userRole = dashboardsData?.data?.userRole;
  const newPatientsData = dashboardsData?.data?.cardsData?.newPatientsPerDay;

  useEffect(() => {
    if (dashboardsIsLoading) {
      return;
    }
    console.log("dashboardsData:", dashboardsData);
    if (!dashboardsData?.success) {
      console.error("Dashboard error: Invalid data structure");
      alert(
        "An issue occurred while loading the dashboard. Please refresh the page",
        "error"
      );
      return;
    }

    if (dashboardsData && dashboardsData.success) {
      setActiveVsNonActive([
        dashboardsData.data.cardsData.activePatients || 0,
        dashboardsData.data.cardsData.nonActivePatients || 0,
      ]);
      setDeviceStatus([
        dashboardsData.data.cardsData.activeDevices || 0,
        dashboardsData.data.cardsData.unassignedDevices || 0,
      ]);
      setCards((prevCards) =>
        prevCards.map((card) => ({
          ...card,
          value: dashboardsData.data.cardsData[card.id] || 0,
        }))
      );
    }
  }, [dashboardsData, dashboardsIsLoading]);
  const treatmentSuccessful =
    dashboardsData?.data?.cardsData?.treatmentSuccessful || 0;
  const totalTreatments = dashboardsData?.data?.cardsData?.treatmentCount || 0;
  const treatmentIncomplete = totalTreatments - treatmentSuccessful;
  const treatmentSeries = [treatmentSuccessful, treatmentIncomplete];
  const patientsByCenter =
    dashboardsData?.data?.cardsData?.patientsByCenter || [];
  const centerCategories = patientsByCenter.map((item: any) => item.siteName);
  const patientsSeriesData = patientsByCenter.map((item: any) =>
    Number(item.patientCount)
  );
  const errorStatsByType =
    dashboardsData?.data?.cardsData?.errorStatsByType || [];

  const errorLabels = errorStatsByType.map(
    (item: any) => DeviceErrorMessages[item.errorCode] || item.errorCode
  );
  const errorSeries = errorStatsByType.map((item: any) => item.count);

  const errorChartOptions = {
    ...chartConfiguration.coloredDonutChart,
    labels: errorLabels,
  };
  const NumberOfPatientsByCenterSeries = [
    {
      name: "Patients",
      data: patientsSeriesData,
    },
  ];
  const updatedBarChartOptions = {
    ...chartConfiguration.barChart,
    xaxis: {
      ...chartConfiguration.barChart.xaxis,
      categories: centerCategories,
    },
  };
  const formattedDates =
    newPatientsData?.dates?.map((dateStr: string | number | Date) => {
      const dateObj = new Date(dateStr);
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const dayName = dayNames[dateObj.getDay()];
      const dayNumber = dateObj.getDate();
      return `${dayName} ${dayNumber}`;
    }) || chartConfiguration.lineChart.xaxis.categories;

  const lineChartOptions = {
    ...chartConfiguration.lineChart,
    xaxis: {
      ...chartConfiguration.lineChart.xaxis,
      categories: formattedDates,
      labels: {
        useHTML: true,
      },
    },
  };
  if (dashboardsIsLoading || isCenterDataLoading) {
    return <Loader text={getText("dashboardsIsLoading")} />;
  }

  const newPatientsSeries = [
    {
      name: getText("newPatients"),
      data: newPatientsData?.counts,
    },
  ];
  const handleExportDashboard = async () => {
    try {
      const blob = await exportDashboardData();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Dashboard_Data.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url); // Cleanup URL
    } catch (error) {
      console.error("Error exporting dashboard data:", error);
      alert("Failed to export dashboard data. Please try again.", "error");
    }
  };

  return (
    <>
      <div className="dashboard-container">
        <div className="table-header">
          <TableHeader headerHeadline={t("menu.dashboard")} cards={[]} />
        </div>
        <div className="table-filters-wrapper">
          <RangeDatePicker
            selectedRange={dateFilter}
            setSelectedRange={setDateFilter}
          />
          {user.role !== roles.HCP && (
            <TableFilters
              trashAction={() => setFilters([])}
              filters={filters}
              setFilters={setFilters}
              filterObj={filtersData ? getFilters(filtersData) : {}}
              showFilterChips={true}
            />
          )}
        </div>

        {/* <div className="export-container">
          <ClickableSVG
            Icon={ExportIcone}
            buttonAction={handleExportDashboard}
          />
        </div> */}
        <div className="inner-dashboard-container">
          {userRole === "Admin" && (
            <div className="column-4 mb-3r pr-15">
              <TableHeaderCards
                cards={[cards[0], cards[1]]}
                cardsContainerStyle={"custom-flex-column"}
              />
            </div>
          )}

          <div className="column-8 mb-3r ">
            <div className="custom-row">
              <div className="column-6">
                <GraphCard
                  isLoading={dashboardsIsLoading}
                  title={getText("activeVsNonActive")}
                >
                  <Chart
                    options={chartConfiguration.donutChart as any}
                    series={activeVsNonActive}
                    type="donut"
                    width="90%"
                  />
                </GraphCard>
              </div>
              <div className="column-6">
                <GraphCard
                  isLoading={dashboardsIsLoading}
                  title={getText("treatmentStatus")}
                >
                  <Chart
                    options={chartConfiguration.donutChartTreatment as any}
                    series={treatmentSeries}
                    type="donut"
                    width="90%"
                  />
                </GraphCard>
              </div>
            </div>
          </div>
          <div className="column-4 mb-3r pr-15">
            <TableHeaderCards
              cards={[cards[2], cards[3]]}
              cardsContainerStyle={"custom-flex-column"}
            />
          </div>
          <div className="column-8 mb-3r">
            <GraphCard
              title={getText("newPatientsPerDay")}
              customStyleChildren={"custom-chart"}
            >
              <Chart
                options={lineChartOptions as any}
                series={newPatientsSeries}
                type="line"
                height="100%"
              />
            </GraphCard>
          </div>
          {userRole === "Admin" && (
            <div className="column-12 mb-3r">
              <GraphCard
                title={getText("patientsByCenter")}
                customStyleChildren={"custom-chart"}
              >
                <Chart
                  options={updatedBarChartOptions as any}
                  series={NumberOfPatientsByCenterSeries}
                  type="bar"
                  width="100%"
                  height="100%"
                />
              </GraphCard>
            </div>
          )}
          {userRole === "Admin" && (
            <div className="column-4 pr-15">
              <TableHeaderCards
                cards={[cards[4], cards[5]]}
                cardsContainerStyle={"custom-flex-column"}
                cardStyle={"padding-left-right"}
              />
            </div>
          )}
          {userRole === "Admin" && (
            <div className="column-8 custom-row">
              <div className="column-6">
                <GraphCard title="Devices status">
                  <Chart
                    options={chartConfiguration.donutChart as any}
                    series={deviceStatus}
                    type="donut"
                    width="90%"
                  />
                </GraphCard>
              </div>
              <div className="column-6">
                <GraphCard title="Devices errors">
                  <Chart
                    options={errorChartOptions as any}
                    series={errorSeries}
                    type="donut"
                    width="92%"
                  />
                </GraphCard>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
